import axios from 'axios';
import { mergeAll } from 'ramda';
import { get } from 'object-path';
import { getCsrfToken } from './BrowserUtils';
import uuidv4 from 'uuid/v4';

const defaultConfig = {
  method: 'get',
  withCredentials: true,
  responseType: 'json',
  _retries: 0,
  _retryDelay: 750,
  headers: { 'Updox-Correlation-Id': uuidv4() },
};

/**
 * A base method for making an XHR request
 *
 * @param url    - The server URL that will be used for the request
 * @param method - The request method to be used when making the request
 * @param config - An object representing configurable properties for the request
 *    These are some common properties, the rest can be found here: [https://github.com/axios/axios#request-config]
 *    {
 *      method: 'get',                 - default 'get'
 *      baseURL: 'https://some-domain.com/api/',
 *      url: '/user',
 *      headers: {'X-Requested-With': 'XMLHttpRequest'},
 *      params: { id: 12345 },
 *      data: { firstName: 'Fred' },   - the data to be sent as the request body (for method: 'put', 'post', 'patch')
 *      responseType: 'json',          - default 'json'
 *      responseEncoding: 'utf8',      - default 'utf8'
 *      timeout: 1000                  - default is `0` (no timeout)
 *    }
 *
 *    Additionally, Messenger specific properties (starting with an underscore)
 *    {
 *      ...,
 *      _retries: 3,      - default 0, The number of times the request should be retried should it fail
 *      _retryDelay: 2000 - default 750, The number of milliseconds to wait before retrying,
 *      _retryCallback    - a function that is called when a retry is attempted, passing the retry attempt number
 *    }
 *
 * @returns - Promise that resolves to an object containing the following properties
 *    {
 *      data: {},            - the response that was provided by the server
 *      status: 200,         - the HTTP status code from the server response
 *      statusText: 'OK',    - the HTTP status message from the server response
 *      headers: {},         - the headers that the server responded with (lower cased)
 *      config: {},          - the config that was provided to `axios` for the request
 *      request: {}          - the request (XMLHttpRequest) that generated this response
 *    }
 */
export const httpRequest = async (url, method, config) => {
  config['X-XSRF-TOKEN'] = getCsrfToken();
  const _config = mergeAll([defaultConfig, config || {}, { url, method }]);

  try {
    return await axios(_config);
  } catch (e) {
    const retries = get(_config, '_retries');
    if (retries > 0) {
      config['_retries'] = retries - 1;
      await new Promise(resolve => setTimeout(resolve, get(_config, '_retryDelay')));

      const retryCallback = config['_retryCallback'];
      if (retryCallback) {
        retryCallback(config['_retries']);
      }

      return await httpRequest(url, method, config);
    }
    throw e;
  }
};

export const httpGet = (url, config) => {
  return httpRequest(url, 'get', config);
};

export const httpGetBlob = (url, config = {}) => {
  config['responseType'] = 'blob';
  return httpRequest(url, 'get', config);
};

export const httpPut = (url, body, config = {}) => {
  config['data'] = body;
  return httpRequest(url, 'put', config);
};

export const httpPost = (url, body, config = {}) => {
  config['data'] = body;
  return httpRequest(url, 'post', config);
};

export const httpDelete = (url, config) => {
  return httpRequest(url, 'delete', config);
};
